import { useState } from "react";
import { matlab } from "@quanta/calcscript";

type ToolboxPackage = (typeof matlab.builtinToolboxes)[number];

type ViewState = { kind: "list" } | { kind: "detail"; toolbox: ToolboxPackage };

export function MarketplacePanel() {
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewState>({ kind: "list" });
  const [installedIds, setInstalledIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem("quanta-installed-toolboxes");
      return saved ? new Set(JSON.parse(saved)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const toggleInstall = (id: string) => {
    setInstalledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem(
        "quanta-installed-toolboxes",
        JSON.stringify([...next]),
      );
      return next;
    });
  };

  const filtered = matlab.builtinToolboxes.filter(
    (tb) =>
      !search ||
      tb.title.toLowerCase().includes(search.toLowerCase()) ||
      tb.tags.some((t) => t.toLowerCase().includes(search.toLowerCase())) ||
      tb.description.toLowerCase().includes(search.toLowerCase()),
  );

  if (view.kind === "detail") {
    const tb = view.toolbox;
    const installed = installedIds.has(tb.id);
    return (
      <div className="marketplace-panel">
        <button
          className="btn-secondary"
          style={{ marginBottom: 12 }}
          onClick={() => setView({ kind: "list" })}
        >
          &larr; Back to Marketplace
        </button>
        <h2>{tb.title}</h2>
        <p style={{ color: "var(--text-secondary)", marginBottom: 8 }}>
          v{tb.version} &middot; {tb.builtinFunctions.length} built-in functions
        </p>
        <p>{tb.description}</p>

        <button
          className={installed ? "btn-secondary" : "btn-primary"}
          style={{ marginTop: 12, marginBottom: 16 }}
          onClick={() => toggleInstall(tb.id)}
        >
          {installed ? "Uninstall" : "Install Toolbox"}
        </button>

        <h3>Built-in Functions</h3>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 16,
          }}
        >
          {tb.builtinFunctions.map((fn) => (
            <code
              key={fn}
              style={{
                background: "var(--bg-secondary, #1e1e2e)",
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: "0.85em",
              }}
            >
              {fn}
            </code>
          ))}
        </div>

        {tb.functions.length > 0 && (
          <>
            <h3>Function Reference</h3>
            {tb.functions.map((fn) => (
              <div
                key={fn.name}
                style={{
                  borderLeft: "3px solid var(--accent, #7c3aed)",
                  paddingLeft: 12,
                  marginBottom: 12,
                }}
              >
                <strong>
                  <code>{fn.name}</code>
                </strong>
                <pre
                  style={{
                    fontSize: "0.85em",
                    margin: "4px 0",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {fn.synopsis}
                </pre>
                <p style={{ margin: "4px 0", fontSize: "0.9em" }}>
                  {fn.description}
                </p>
                {fn.examples && fn.examples.length > 0 && (
                  <div style={{ fontSize: "0.85em" }}>
                    {fn.examples.map((ex, i) => (
                      <code
                        key={i}
                        style={{ display: "block", margin: "2px 0" }}
                      >
                        {ex.code}
                        {ex.result ? ` → ${ex.result}` : ""}
                      </code>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tb.examples.length > 0 && (
          <>
            <h3>Examples</h3>
            {tb.examples.map((ex, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <h4>{ex.title}</h4>
                <p style={{ fontSize: "0.9em" }}>{ex.description}</p>
                <pre
                  style={{
                    background: "var(--bg-secondary, #1e1e2e)",
                    padding: 12,
                    borderRadius: 6,
                    fontSize: "0.85em",
                    whiteSpace: "pre-wrap",
                    overflowX: "auto",
                  }}
                >
                  {ex.code}
                </pre>
              </div>
            ))}
          </>
        )}

        {installed && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 8,
              background: "var(--bg-secondary, #1e1e2e)",
            }}
          >
            <h4>Usage</h4>
            <p style={{ fontSize: "0.9em" }}>
              All built-in functions are available immediately in the
              calculator. To load the helper scripts, type:
            </p>
            <code
              style={{
                display: "block",
                margin: "8px 0",
                padding: 8,
                borderRadius: 4,
                background: "var(--bg-primary, #0d0d1a)",
              }}
            >
              loadToolbox('{tb.id}')
            </code>
            <p style={{ fontSize: "0.85em", color: "var(--text-secondary)" }}>
              This loads helper functions like{" "}
              {tb.code
                .match(/function\s+(?:\[[^\]]*\]\s*=\s*)?(\w+)/g)
                ?.slice(0, 3)
                .map((m) => m.replace(/function\s+(?:\[[^\]]*\]\s*=\s*)?/, ""))
                .join(", ")}{" "}
              into your environment.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="marketplace-panel">
      <div className="marketplace-header">
        <h2>Toolbox Marketplace</h2>
        <div className="marketplace-search">
          <input
            type="search"
            placeholder="Search toolboxes..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="marketplace-categories">
        {filtered.map((tb) => (
          <div
            key={tb.id}
            className="category-card"
            onClick={() => setView({ kind: "detail", toolbox: tb })}
            style={{ cursor: "pointer" }}
          >
            <div className="category-info">
              <h4>
                {tb.title}
                {installedIds.has(tb.id) && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: "0.75em",
                      color: "var(--accent, #7c3aed)",
                    }}
                  >
                    Installed
                  </span>
                )}
              </h4>
              <p>{tb.description}</p>
              <span className="category-count">
                {tb.builtinFunctions.length} functions &middot; v{tb.version}
              </span>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginTop: 6,
                }}
              >
                {tb.tags.slice(0, 4).map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: "0.75em",
                      padding: "1px 6px",
                      borderRadius: 3,
                      background: "var(--bg-secondary, #1e1e2e)",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
